import React from "react";
import { MapPin, TrainFront, Clock, Navigation } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/data/site";
import { Reveal, Kicker } from "./Reveal";

export default function Location() {
  const { t } = useLang();
  const rows = [
    { icon: MapPin, label: t.addressLabel, value: `${CONTACT.address}, ${CONTACT.city}` },
    { icon: TrainFront, label: t.metroLabel, value: CONTACT.metro },
    { icon: Clock, label: t.hoursLabel, value: `${t.open} · ${t.hours}` },
  ];

  return (
    <section id="location" className="relative py-24 md:py-36" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Kicker className="mb-5">{t.locKicker}</Kicker>
        <h2 className="mb-14 max-w-2xl font-serif text-5xl leading-[0.95] tracking-tight text-[var(--text)] md:text-7xl">
          {t.locTitle}
        </h2>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-stretch">
          <Reveal className="flex flex-col justify-between gap-8">
            <div className="space-y-2">
              {rows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-5 border-b border-[var(--gold)]/15 py-6"
                    data-testid={`location-row-${i}`}
                  >
                    <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--gold)]/40 text-[var(--gold)]">
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-[var(--text-3)]">
                        {r.label}
                      </p>
                      <p className="mt-1 font-serif text-2xl text-[var(--text)]">{r.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <a
              href={CONTACT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-directions-btn"
              className="inline-flex w-fit items-center gap-3 bg-[var(--gold)] px-8 py-4 font-sans text-sm font-semibold tracking-wide text-[var(--bg)] transition-transform hover:scale-[1.02]"
            >
              <Navigation size={17} /> {t.directions}
            </a>
          </Reveal>

          <Reveal delay={0.15} className="min-h-[380px] overflow-hidden border border-[var(--gold)]/20">
            <iframe
              title="Le Goût du Fouta - Google Maps"
              src={`https://www.google.com/maps?q=${CONTACT.mapsQuery}&output=embed`}
              className="h-full min-h-[380px] w-full"
              style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
